import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Typography, Box, IconButton, Button, Chip,
  Tooltip, alpha, useTheme,
} from '@mui/material';
import {
  Edit as EditIcon, Archive as ArchiveIcon, Restore as RestoreIcon, Add as AddIcon,
  Business as BusinessIcon, Assignment as AssignmentIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon, AccessTime as AccessTimeIcon,
  Visibility as VisibilityIcon,
  PlayArrow as InProgressIcon,
  ViewList as ViewListIcon,
  ViewKanban as ViewKanbanIcon,
} from '@mui/icons-material';
import RequirementKanban from './RequirementKanban';
import {
  REQUIREMENT_STATUS_FILTER_OPTIONS,
  getRequirementStatusMeta,
  getRequirementStatusSortRank,
  resolveRequirementStatus,
  compareRequirementListItems,
} from '../../../utils/requirementStatus';
import {
  canUpdateRequirementStatus,
  canAccessPartnershipManagement,
  canEditRequirement,
  canCancelRequirement,
} from '../../../utils/partnershipPermissions';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import ListFilterBar from '../../common/ListFilterBar';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import StatCards from '../../common/StatCards';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { REQUIREMENT_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';
import { ACCENT, TABLE_SURFACE_2 } from '../../common/SaaSTokens';

import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const RL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes bar-sweep { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
`;

const getStatusNeon = (s) => getRequirementStatusMeta(s).color;

const RequirementList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();
  

  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem('requirementListView') === 'kanban' ? 'kanban' : 'table';
    } catch {
      return 'table';
    }
  });
  const [updatingRequirementId, setUpdatingRequirementId] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [selectedReq, setSelectedReq] = useState(null);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);

  const canCreateRequirement = Boolean(currentUser);
  const canDragKanban = canUpdateRequirementStatus(currentUser);
  const isPartnershipStaff = canAccessPartnershipManagement(currentUser);

  const filterOptions = [
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      searchable: true,
      options: [
        { value: 'archived', label: 'Archived' },
        ...REQUIREMENT_STATUS_FILTER_OPTIONS.filter((option) => option.value !== 'archived'),
      ],
    },
    {
      name: 'is_emergency',
      label: 'Emergency',
      type: 'select',
      searchable: true,
      options: [
        { value: 'true', label: 'Emergency only' },
        { value: 'false', label: 'Non-emergency' },
      ],
    },
    {
      name: 'company_id',
      label: 'Company',
      type: 'select',
      searchable: true,
      options: companyOptions.map((company) => ({
        value: String(company.id),
        label: company.name || `#${company.id}`,
      })),
    },
    ...(isPartnershipStaff ? [{
      name: 'responsible_id',
      label: 'Assigned to',
      type: 'select',
      searchable: true,
      options: userOptions.map((user) => ({
        value: String(user.id),
        label: user.name || user.email || `#${user.id}`,
      })),
    }] : []),
  ];

  useEffect(() => {
    const cssId = 'reqlist-ds-css';
    if (!document.getElementById(cssId)) { const el = document.createElement('style'); el.id = cssId; el.innerHTML = RL_CSS; document.head.appendChild(el); }
  }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    fetchRequirements({ forceRefresh: Boolean(location.state?.refreshList) });
    loadFilterOptions();
  }, [currentUser, location.pathname, location.state?.refreshList]);

  const loadFilterOptions = async () => {
    try {
      const requests = [api.get('/companies/for-claim-form')];
      if (isPartnershipStaff) requests.push(api.get('/users?active_only=1'));
      const [companiesRes, usersRes] = await Promise.all(requests);
      setCompanyOptions(Array.isArray(companiesRes.data) ? companiesRes.data : []);
      if (isPartnershipStaff && usersRes) {
        setUserOptions(Array.isArray(usersRes.data) ? usersRes.data : []);
      }
    } catch {
      setCompanyOptions([]);
      setUserOptions([]);
    }
  };

  const fetchRequirements = async ({ forceRefresh = false, archived = false } = {}) => {
    try {
      setLoading(true);
      const r = await api.get('/requirements', {
        ...(forceRefresh ? { cache: false } : {}),
        ...(archived ? { params: { archived: 1 } } : {}),
      });
      setRequirements(r.data); setError(null);
    } catch { setError('Failed to load requirements. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleRestore = async (requirement) => {
    try {
      await api.post(`/requirements/${requirement.id}/restore`);
      setRequirements((prev) => prev.filter((item) => item.id !== requirement.id));
      setSuccess(`Requirement "${requirement.wanted_item || 'Requirement'}" has been restored successfully!`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to restore requirement. Please try again.');
    }
  };

  const handleCancel = (req) => {
    setSelectedReq(req);
    setCancelDialogOpen(true);
  };

  const confirmCancel = async () => {
    if (!selectedReq) return;
    try {
      setCancelLoading(true);
      await api.post(`/requirements/${selectedReq.id}/archive`);
      setRequirements((prev) => prev.filter((requirement) => requirement.id !== selectedReq.id));
      setCancelDialogOpen(false);
      setSelectedReq(null);
      setSuccess(`Requirement "${selectedReq.wanted_item || 'Request'}" has been archived successfully!`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to archive requirement. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleBulkCancel = async () => {
    if (!selectedIds?.length) {
      setError('No selected requirements to archive.');
      return;
    }

    try {
      await Promise.all(selectedIds.map((requirementId) => (
        api.post(`/requirements/${Number(requirementId)}/archive`)
      )));

      const selectedSet = new Set(selectedIds.map(String));
      setRequirements((prev) => prev.filter((row) => !selectedSet.has(String(row.id))));
      setError(null);
      setSuccess(`${selectedIds.length} requirement(s) archived successfully.`);
      clearSelection();
      fetchRequirements({ forceRefresh: true });
    } catch (err) {
      setError(
        err?.response?.data?.message
        || Object.values(err?.response?.data?.errors || {}).flat()?.[0]
        || 'Bulk archive failed.',
      );
    }
  };

  const handleFilterChange = (newFilters) => {
    const archivedViewChanged = (filters.status === 'archived') !== (newFilters.status === 'archived');
    const nextActive = newFilters.status ? newFilters.status : 'all';
    setActiveStatFilter(nextActive);
    setFilters(newFilters);
    setPage(0);
    if (newFilters.status === 'archived') setViewMode('table');
    if (archivedViewChanged) {
      clearSelection();
      fetchRequirements({ forceRefresh: true, archived: newFilters.status === 'archived' });
    }
  };

  const handleClearFilters = () => {
    const wasArchived = filters.status === 'archived';
    setFilters({});
    setActiveStatFilter('all');
    setSearchTerm('');
    setPage(0);
    if (wasArchived) fetchRequirements({ forceRefresh: true });
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem('requirementListView', mode);
    } catch {
      /* ignore */
    }
  };

  const handleKanbanStatusChange = async (requirement, newStatus) => {
    const prev = requirements;
    setRequirements((list) =>
      list.map((r) => (r.id === requirement.id ? { ...r, status: newStatus } : r))
    );
    setUpdatingRequirementId(requirement.id);
    try {
      const { data } = await api.put(`/requirements/${requirement.id}`, { status: newStatus });
      setRequirements((list) =>
        list.map((r) => (r.id === requirement.id ? data : r))
      );
      setSuccess(`Moved "${requirement.wanted_item || 'Request'}" to ${getRequirementStatusMeta(newStatus).label}`);
    } catch (err) {
      setRequirements(prev);
      setError(err.response?.data?.message || 'Failed to update requirement status');
    } finally {
      setUpdatingRequirementId(null);
    }
  };

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'not_started', 'in_progress', 'completed'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);

    const nextFilters = { ...filters };
    if (nextFilter === 'all') {
      delete nextFilters.status;
    } else {
      nextFilters.status = nextFilter;
    }
    setFilters(nextFilters);
    setPage(0);
  };

  const activeStatIndex =
    activeStatFilter === 'not_started' ? 1 :
    activeStatFilter === 'in_progress' ? 2 :
    activeStatFilter === 'completed' ? 3 :
    0;

  const filteredRequirements = requirements.filter(req => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      (req.wanted_item?.toLowerCase() ?? '').includes(q) ||
      (req.purpose?.toLowerCase() ?? '').includes(q) ||
      (req.company_name?.toLowerCase() ?? '').includes(q) ||
      (req.responsible?.name?.toLowerCase() ?? '').includes(q);
    const matchesFilters = Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      if (key === 'status') return value === 'archived' ? Boolean(req.archived_at) : resolveRequirementStatus(req) === value;
      if (key === 'is_emergency') return String(Boolean(req.is_emergency)) === value;
      if (key === 'company_id') return String(req.company_id ?? '') === String(value);
      if (key === 'responsible_id') return String(req.responsible_id ?? '') === String(value);
      return true;
    });
    return matchesSearch && matchesFilters;
  }).sort(compareRequirementListItems);

  const statsData = [
    { icon: AssignmentIcon, value: requirements.length, label: 'Total requests', color: 'info' },
    { icon: AccessTimeIcon, value: requirements.filter(r => !r.archived_at && r.status === 'not_started').length, label: 'Not started', color: 'warning' },
    { icon: InProgressIcon, value: requirements.filter(r => !r.archived_at && r.status === 'in_progress').length, label: 'In progress', color: 'secondary' },
    { icon: CheckCircleIcon, value: requirements.filter(r => !r.archived_at && r.status === 'completed').length, label: 'Completed', color: 'success' },
  ];

  const columns = [
    {
      key: 'wanted_item', label: 'Request', icon: AssignmentIcon, minWidth: 280,
      sortAccessor: (req) => req.wanted_item || '',
      render: (req) => (
        <Box>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.875rem', color: 'rgba(226,232,240,0.9)', mb: 0.25 }}>{req.wanted_item || '—'}</Typography>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: 'rgba(148,163,184,0.85)' }}>
            {req.amount ? `Amount: ${req.amount}` : ''}
            {req.amount && req.is_emergency ? ' · ' : ''}
            {req.is_emergency && (
              <Box component="span" sx={{ color: '#F87171', fontWeight: 700 }}>
                Emergency
              </Box>
            )}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'company_name', label: 'Company', icon: BusinessIcon, minWidth: 180,
      sortAccessor: (req) => req.company_name || '',
      render: (req) => (
        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: 'rgba(226,232,240,0.8)' }}>
          {req.company_name || '—'}
        </Typography>
      ),
    },
    {
      key: 'responsible_name', label: 'Assigned To', icon: PersonIcon, minWidth: 160,
      sortAccessor: (req) => (req.assignees || []).map((member) => member.name).join(', ') || req.responsible?.name || '',
      render: (req) => (
        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: 'rgba(226,232,240,0.8)' }}>
          {(req.assignees || []).map((member) => member.name).join(', ') || req.responsible?.name || '—'}
        </Typography>
      ),
    },
    {
      key: 'status', label: 'Status', minWidth: 140,
      sortAccessor: (req) => getRequirementStatusSortRank(req),
      render: (req) => {
        const effectiveStatus = resolveRequirementStatus(req);
        const n = getStatusNeon(effectiveStatus);
        const label = effectiveStatus.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        return (
          <Chip label={label} size="small"
            sx={{ height: 22, fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', fontWeight: 600, bgcolor: alpha(n, 0.1), color: n, border: `1px solid ${alpha(n, 0.35)}`, boxShadow: `0 0 8px ${alpha(n, 0.15)}`, '& .MuiChip-label': { px: 1 } }}
          />
        );
      },
    },
    {
      key: 'actions', label: 'Actions', sortable: false, align: 'right', minWidth: 130,
      render: (req) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="View Requirement" arrow>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/requirements/${req.id}`); }}
              sx={{
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                }
              }}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canEditRequirement(currentUser, req) && !req.archived_at && (
            <Tooltip title="Edit Requirement" arrow>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/requirements/edit/${req.id}`); }}
                sx={{
                  color: theme.palette.warning.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                  }
                }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {isPartnershipStaff && req.archived_at && (
            <Tooltip title="Restore Requirement" arrow>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRestore(req); }}
                sx={{ color: theme.palette.success.main, '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.1) } }}>
                <RestoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canCancelRequirement(currentUser, req) && (
            <Tooltip title="Archive Requirement" arrow>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCancel(req); }}
                sx={{
                  color: theme.palette.error.main,
                  '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) },
                }}>
                <ArchiveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (!currentUser) return null;
  if (loading) return(
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading requirements list" size={56} variant="primary" />
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <Header
        title="Requirements Management"
        subtitle="Track and manage project requirements and assignments"
        icon={AssignmentIcon}
        actionButton={(
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {[{ mode: 'table', Icon: ViewListIcon, label: 'List view' }, { mode: 'kanban', Icon: ViewKanbanIcon, label: 'Kanban view' }].map(({ mode, Icon, label }) => (
              <Tooltip key={mode} title={label} arrow>
                <IconButton
                  onClick={() => handleViewModeChange(mode)}
                  sx={{
                    borderRadius: '9px',
                    width: 36,
                    height: 36,
                    background: viewMode === mode ? alpha(ACCENT, 0.16) : alpha(TABLE_SURFACE_2, 0.5),
                    border: `1px solid ${alpha(ACCENT, viewMode === mode ? 0.4 : 0.15)}`,
                    color: viewMode === mode ? ACCENT : alpha(ACCENT, 0.5),
                    transition: 'all 0.2s ease',
                    '&:hover': { background: alpha(ACCENT, 0.1), borderColor: alpha(ACCENT, 0.35), color: ACCENT },
                  }}
                >
                  <Icon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            ))}
            {canCreateRequirement && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/requirements/add')}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.22)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.34)' },
                  borderRadius: 2,
                  px: 2.5,
                  py: 1.5,
                  minWidth: { xs: '100%', sm: 170 },
                  flex: { xs: '1 1 100%', sm: '0 1 auto' },
                }}
              >
                Add Requirement
              </Button>
            )}
          </Box>
        )}
      />

      <FeedbackMessage message={error} severity="error" onClose={() => setError(null)} />
      <FeedbackMessage message={success} severity="success" onClose={() => setSuccess(null)} />

      <StatCards stats={statsData} onCardClick={handleStatCardClick} activeIndex={activeStatIndex} />

      <ListFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        filterOptions={filterOptions}
        searchPlaceholder="Search by request, purpose, company, or assignee..."
      />

      {viewMode === 'table' && filters.status !== 'archived' && (
        <ListBulkToolbar
          config={REQUIREMENT_BULK_CONFIG}
          currentUser={currentUser}
          selectedIds={selectedIds}
          onClearSelection={clearSelection}
          {...createBulkHandlers({
            endpoint: REQUIREMENT_BULK_CONFIG.endpoint,
            setItems: setRequirements,
            selectedIds,
            clearSelection,
            setSuccess,
            successLabel: 'requirement(s)',
            refetch: fetchRequirements,
            setError,
          })}
          onDeleteSuccess={handleBulkCancel}
        />
      )}

      {viewMode === 'table' ? (
        <TableList
          columns={columns}
          data={filteredRequirements}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          {...(filters.status !== 'archived' ? bulkTableProps(REQUIREMENT_BULK_CONFIG, currentUser, selectedIds, setSelectedIds) : {})}
          loading={loading}
          emptyState={{
            icon: <AssignmentIcon />,
            title: 'No requirements found',
            description: searchTerm || Object.keys(filters).length > 0
              ? 'Try adjusting your filters to see more requirements.'
              : 'Get started by creating your first requirement.',
          }}
          onRowClick={(row) => navigate(`/requirements/${row.id}`)}
        />
      ) : filteredRequirements.length === 0 ? (
        <Box
          sx={{
            py: 8,
            textAlign: 'center',
            borderRadius: '14px',
            border: '1px dashed rgba(148,163,184,0.25)',
            bgcolor: 'rgba(15,23,42,0.35)',
          }}
        >
          <AssignmentIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.25)', mb: 1 }} />
          <Typography sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>No requirements found</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem' }}>
            {searchTerm || Object.keys(filters).length > 0
              ? 'Try adjusting your filters.'
              : 'Create a requirement or switch to list view.'}
          </Typography>
        </Box>
      ) : (
        <RequirementKanban
          requirements={filteredRequirements}
          user={currentUser}
          canDrag={() => canDragKanban}
          onStatusChange={handleKanbanStatusChange}
          onOpenRequirement={(r) => navigate(`/requirements/${r.id}`)}
          onEditRequirement={(r) => {
            if (canEditRequirement(currentUser, r)) navigate(`/requirements/edit/${r.id}`);
          }}
          onCancelRequirement={handleCancel}
          updatingRequirementId={updatingRequirementId}
        />
      )}

      <ConfirmDeleteDialog
        open={cancelDialogOpen}
        onClose={() => { setCancelDialogOpen(false); setSelectedReq(null); }}
        onConfirm={confirmCancel}
        loading={cancelLoading}
        title="Archive Requirement"
        itemType="requirement"
        itemName={selectedReq?.wanted_item}
        description="The requirement will be archived (soft-deleted) and retained in the database for recovery."
        confirmLabel="Archive Requirement"
        actionVerb="archive"
      />

    </Container>
  );
};

export default RequirementList;
